import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SchemaMarkupProps {
  type: 'LocalBusiness' | 'Service' | 'Review' | 'FAQ' | 'HowTo';
  data: any;
}

export function SchemaMarkup({ type, data }: SchemaMarkupProps) {
  const generateSchema = () => {
    switch (type) {
      case 'LocalBusiness':
        return {
          '@context': 'https://schema.org',
          '@type': 'Plumber',
          '@id': `https://nearplumbers.com/plumbers/${data.id}`,
          name: data.business_name,
          image: data.logo_url,
          telephone: data.phone,
          email: data.email,
          url: data.website,
          description: data.description,
          address: {
            '@type': 'PostalAddress',
            addressLocality: data.city,
            addressRegion: data.state,
            postalCode: data.zip_code
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: data.latitude,
            longitude: data.longitude
          },
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: data.rating,
            reviewCount: data.reviews?.length || 0
          },
          priceRange: '$$',
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
              'Sunday'
            ],
            opens: '00:00',
            closes: '23:59'
          },
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Plumbing Services',
            itemListElement: data.services.map((service: any) => ({
              '@type': 'Offer',
              itemOffered: {
                '@type': 'Service',
                name: service.title,
                description: service.description
              }
            }))
          }
        };

      case 'Service':
        return {
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: data.title,
          description: data.description,
          provider: {
            '@type': 'LocalBusiness',
            name: 'NearPlumbers'
          },
          areaServed: {
            '@type': 'City',
            name: data.city,
            '@id': `https://nearplumbers.com/cities/${data.city_id}`
          },
          serviceType: 'Plumbing',
          termsOfService: 'https://nearplumbers.com/terms',
          hoursAvailable: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'Monday',
              'Tuesday',
              'Wednesday',
              'Thursday',
              'Friday',
              'Saturday',
              'Sunday'
            ],
            opens: '00:00',
            closes: '23:59'
          }
        };

      case 'FAQ':
        return {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: data.questions.map((q: any) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: q.answer
            }
          }))
        };

      case 'HowTo':
        return {
          '@context': 'https://schema.org',
          '@type': 'HowTo',
          name: data.title,
          description: data.description,
          totalTime: data.totalTime,
          estimatedCost: {
            '@type': 'MonetaryAmount',
            currency: 'USD',
            value: data.estimatedCost
          },
          supply: data.supplies.map((supply: string) => ({
            '@type': 'HowToSupply',
            name: supply
          })),
          tool: data.tools.map((tool: string) => ({
            '@type': 'HowToTool',
            name: tool
          })),
          step: data.steps.map((step: any, index: number) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: step.title,
            text: step.description,
            image: step.image
          }))
        };

      case 'Review':
        return {
          '@context': 'https://schema.org',
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: data.rating,
            bestRating: '5',
            worstRating: '1'
          },
          author: {
            '@type': 'Person',
            name: data.author_name
          },
          datePublished: data.created_at,
          reviewBody: data.comment,
          itemReviewed: {
            '@type': 'LocalBusiness',
            name: data.business_name,
            image: data.business_image,
            telephone: data.business_phone,
            address: {
              '@type': 'PostalAddress',
              addressLocality: data.business_city,
              addressRegion: data.business_state
            }
          }
        };

      default:
        return {};
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(generateSchema())}
      </script>
    </Helmet>
  );
}